import React from 'react';
import { jsx } from '@emotion/core';
import Flex from '../../components/layout/Flex';
import Avatar from '../../components/avatar/Avatar';
import Grid, { GridItem } from '../../components/layout/Grid';
import theme from '../../theme/theme';
import Separator from '../../components/separator/Separator';
import Button from '../../components/button/Button';
import Stack from '../../components/stack/Stack';
import StatementModal from './components/StatementModal';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toastify from '../../components/toastify/Toastify';
import Accordion from '../../components/accordion/Accordion';
import LabeledValue from '../../components/labeledValue/LabeledValue';
import {
  useGenerate_Signeds3_Request_For_DownloadMutation,
  useProspectDetailQuery,
  useSend_Welcome_PackMutation,
} from '../../generated/graphql';
import Goal from '../prospects/components/Goal';
import InfoCard from '../../components/card/InfoCard';
import CustomerDetailModal, {
  ICustomerDetailModalConfig,
} from './components/CustomerDetailModal';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import Loader from '../../components/loader/Loader';
import RedemptionModal from './components/RedemptionModal';
import ActionSelect from '../../components/select/ActionSelect';
import {
  customer_type,
  AccountType,
  individualResidentialInfo,
  jointResidentialInfo,
  individualOccupationInfo,
  jointOccupationInfo,
  individualBioInfo,
  jointBioInfo,
  nextofkinInfo,
  beneficiaryInfo,
  trustBioInfo,
  trustInfo
} from '../../shared/sections';
import { useAuthState } from '../../context/auth';
/**@jsx jsx*/

function formatLabel(label: string): string {
  if (!label) {
    return '';
  }
  if (['id', 'ID'].includes(label.split('_')[0])) {
    return `IDENTIFICATION ${label.split('_')[1]}`
  }
  if (label.slice(-2) === 'jt') {
    return label.slice(0, -3)
  }
  return label.split('_').join(' ');
}

function formatAccountType(label: string | undefined) {
  if (label === customer_type.individual) {
    return AccountType.individual;
  }
  if (label === customer_type.trust) {
    return AccountType.trust;
  }
  if (label === customer_type.joint) {
    return AccountType.joint;
  }
}
export function extractObjectFields(fields: string[], object: any) {
  if (!object) {
    return;
  }
  let newObject: any = {};
  fields.forEach((field) => {
    newObject[field] = object[field];
  });
  return newObject;
}

export const InfoDisplayGrid: React.FC<{
  columns?: number;
  object?: { [key: string]: any };
  ignore?: string[];
  extraAttributes?: { [key: string]: any }[];
}> = ({ columns = 3, object = {}, ignore, extraAttributes }) => {
  const keys = Object.keys(object);
  const a = keys.map((key: string) => {
    const value = object[key];

    if (value == null) {
      return { label: key, value: '' };
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return { label: key, value };
    }

    if (typeof value === 'object') {
      return {
        label: key,
        value: value?.name ?? '',
      };
    }

    return { label: key, value: '' };
  });

  return (
    <Grid lg={columns}>
      {a.map((data, index) => {
        if (ignore && ignore.includes(data.label)) {
          return null;
        }
        if (data.label === 'id' || data.label === 'guid') {
          return null;
        }
        return (
          <LabeledValue
            key={index}
            label={formatLabel(data.label)}
            value={data.value}></LabeledValue>
        );
      })}
      {
        extraAttributes?.map((data: any, index) => {
          return (
            <LabeledValue
              key={index}
              label={formatLabel(data.label)}
              value={data.value}></LabeledValue>
          );
        })
      }
    </Grid>
  );
};


const ViewCustomer: React.FC = () => {
  const [statementModalIsOpen, setStatementModalIsOpen] = React.useState(false);
  const [detailModalIsOpen, setDetailModalIsOpen] = React.useState(false);
  const [redemptionModalIsOpen, setRedemptionModalIsOpen] = React.useState(false);
  const [detailModalData, setDetailModalData] = React.useState<
    ICustomerDetailModalConfig
  >({
    title: '',
  });

  const [generateSignedS3RequestForDownload] = useGenerate_Signeds3_Request_For_DownloadMutation();
  const [idImageUrls, setIdImageUrls] = React.useState<{ [key: number]: string }>({});
  const [signatureUrl, setSignatureUrl] = React.useState<string>('');
  const [kycUrls, setKycUrls] = React.useState<Array<{ url: string; type: string }>>([]);


  const [sendWelcomePack] = useSend_Welcome_PackMutation();

  const { id } = useParams<{ id: string }>();
  const { role } = useAuthState()

  const { data: prospect, loading } = useProspectDetailQuery({
    variables: {
      id,
    },
    fetchPolicy: 'cache-and-network',
  });

  const notify = (success: boolean, message: string) => {
    toast(<Toastify {...{ success, message }} />, {
      position: 'top-right',
      autoClose: 7000,
      progressStyle: {
        background: success ? theme.colors.green[200] : theme.colors.red[200],
      },
    });
  };

  const sendWelcomePackHandler = async () => {
    try {
      const { data } = await sendWelcomePack({
        variables: {
          id: prospect?.member?.[0].id,
        },
        awaitRefetchQueries: true,
      });

      if (data?.sendWelcomePack.success) {
        notify(true, 'Welcome Pack sent successfully');
      } else {
        notify(true, 'Welcome Pack sending failed');
      }
    } catch (err) {
      notify(true, 'Welcome Pack sending failed');
    }
  };

  const handleEditCustomer = (id: number) => {
    if (prospectData?.account_type === customer_type.individual) {
      return `/dashboard/customers/${id}/edit/individual`
    }
    if (prospectData?.account_type === customer_type.joint) {
      return `/dashboard/customers/${id}/edit/joint`
    }
    if (prospectData?.account_type === customer_type.trust) {
      return `/dashboard/customers/${id}/edit/trust`
    }
  }

  const prospectData = prospect?.member?.[0];

    const getSignedUrl = React.useCallback(async (filename: string): Promise<string> => {
    if (!filename || typeof filename !== 'string') return '';
    
    // If it's already a full URL (old base64 or http), return as is
    if (filename.startsWith('http') || filename.startsWith('data:')) {
      return filename;
    }

    try {
      const ext = filename.split('.').pop() || '';
      const { data } = await generateSignedS3RequestForDownload({
        variables: { filename, filetype: ext },
      });
      return data?.generateSignedS3RequestForDownload?.signedRequest || '';
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      return '';
    }
  }, [generateSignedS3RequestForDownload]);


React.useEffect(() => {
  const loadSignedUrls = async () => {
    if (!prospectData) return;

    // Load ID images
    const idUrls: { [key: number]: string } = {};
    for (const identification of prospectData.member_identifications) {
      if (identification.identification_card_image_url) {
        const url = await getSignedUrl(identification.identification_card_image_url);
        if (url) idUrls[identification.id] = url;
      }
    }
    setIdImageUrls(idUrls);

    if (prospectData.signature) {
      const url = await getSignedUrl(prospectData.signature);
      if (url) setSignatureUrl(url);
    }

    if (prospectData.kyc_form) {
      try {
        if (prospectData.kyc_form.endsWith('.pdf')) {
          const url = await getSignedUrl(prospectData.kyc_form);
          if (url) {
            setKycUrls([{ url, type: 'application/pdf' }]);
          }
        } 
        else {
          const parsed = JSON.parse(prospectData.kyc_form);
          if (Array.isArray(parsed)) {
            const urls = await Promise.all(
              parsed.map(async (item: any) => {
                const filename = typeof item === 'string' ? item : item.filename || item.url;
                
                if (!filename) return null;

                if (filename.startsWith('http') || filename.startsWith('data:')) {
                  return { url: filename, type: item.type || 'application/octet-stream' };
                }

                const url = await getSignedUrl(filename);
                if (!url) return null;

                const ext = filename.split('.').pop() || '';
                const typeMap: Record<string, string> = {
                  pdf: 'application/pdf',
                  png: 'image/png',
                  jpg: 'image/jpeg',
                  jpeg: 'image/jpeg',
                  gif: 'image/gif',
                };

                return { url, type: item.type || typeMap[ext] || 'application/octet-stream' };
              })
            );
            setKycUrls(urls.filter((u): u is { url: string; type: string } => u !== null));
          }
        }
      } catch (error) {
        console.error('Failed to parse KYC forms:', error);
        if (prospectData.kyc_form && !prospectData.kyc_form.startsWith('data:')) {
          const url = await getSignedUrl(prospectData.kyc_form);
          if (url) {
            const ext = prospectData.kyc_form.split('.').pop() || '';
            const typeMap: Record<string, string> = {
              pdf: 'application/pdf',
              png: 'image/png',
              jpg: 'image/jpeg',
              jpeg: 'image/jpeg',
              gif: 'image/gif',
            };
            setKycUrls([{ url, type: typeMap[ext] || 'application/pdf' }]);
          }
        }
      }
    }
  };

  loadSignedUrls();
}, [prospectData, getSignedUrl]);

  // check if customer is a joint or trust account type
  var isJointAccount = prospectData?.account_type === customer_type.joint
  var isTrustAccount = prospectData?.account_type === customer_type.trust
  var isIndividualAndJoint = prospectData?.account_type === customer_type.individual || prospectData?.account_type === customer_type.joint

  // construct source of funds data
  const memberIncomeSources = prospectData?.member_income_sources.filter(src => src.income_source_id !== 5)?.map((item) => item.income_source.name)
    .join(', ');
  const memberContributionSources = prospectData?.member_contribution_sources.filter(src => src.contribution_source_id !== 6)?.map((item) => item.contribution_source.name)
    .join(', ');

  // construct mode of instruction data
  const memberModeOfInstructions = prospectData?.member_mode_of_instruction_communications.filter(src => src.mode_of_instruction_communication_id !== 4)
    ?.map((item) => item.mode_of_instruction_communication.name)
    .join(', ');

  function handleViewCustomerDetail(config: ICustomerDetailModalConfig): void {
    setDetailModalData(config);
    setDetailModalIsOpen(true);
  }
  return (
    <React.Fragment>
      {redemptionModalIsOpen && (
        <RedemptionModal
          onClose={() => {
            setRedemptionModalIsOpen(false);
          }}
        />
      )}
      {statementModalIsOpen && (
        <StatementModal
          onClose={() => {
            setStatementModalIsOpen(false);
          }}
          customerId={id}
        />
      )}
      {detailModalIsOpen && (
        <CustomerDetailModal
          onClose={() => {
            setDetailModalIsOpen(false);
          }}
          title={detailModalData.title}
          data={detailModalData.data}
          render={detailModalData.render}
          ignore={detailModalData.ignore}
        />
      )}
      <SectionTitle gap={48} useBreadCrumb></SectionTitle>

      {loading ? (
        <Flex css={{ height: '200px' }} ai="center" jc="center">
          <Loader
            size="small"
            color={theme.colors.primary}
            label="Loading Customer Data"
          />
        </Flex>
      ) : (
        <Grid>
          <GridItem css={{ paddingLeft: '50px' }} span={10}>
            <div css={{ marginBottom: '48px' }}>
              <Flex ai="flex-end" jc="space-between">
                <Flex ai="center">
                  <Avatar
                    background={theme.colors.blue[50]}
                    css={{
                      marginRight: '16px',
                      color: theme.colors.primary,
                    }}
                    size="md"
                    name={`${prospectData?.user?.first_name ?? ''} ${prospectData?.user?.last_name ?? ''
                      }`}></Avatar>
                  <div>
                    <h2 css={{ fontSize: '14px' }}>{`${prospectData?.user?.first_name ?? ''
                      } ${prospectData?.user?.last_name ?? ''}`}</h2>
                    <p
                      css={{ color: theme.colors.gray[400], fontSize: '12px' }}>
                      {prospectData?.user.email}
                    </p>
                  </div>
                </Flex>
                <Stack spacing={12} isInline>
                  <Button
                    action={() => {
                      setStatementModalIsOpen(true);
                    }}>
                    Get Statement
                  </Button>

                  <Button appearance="outline" action={sendWelcomePackHandler}>
                    {`${prospect?.member[0]?.welcome_pack_sent_at
                      ? 'Resend '
                      : 'Send '
                      }Welcome Pack`}
                  </Button>

                  {role !== 'advisor' &&
                    <Button
                      appearance="outline"
                      path={handleEditCustomer(parseInt(id))}>
                      Edit Customer
                    </Button>
                  }

                  <ActionSelect
                    showBorder
                    label="more"
                    options={[
                      // { label: 'Create Ticket' },
                      // { label: 'Generate Date of Birth' },
                      {
                        label: 'Redeem',
                        action: () => setRedemptionModalIsOpen(true),
                      },
                    ]}
                  />
                </Stack>
              </Flex>
              <Separator />
            </div>

            <div>
              <Stack>
                <Accordion id="acceptedgoals" title="Accepted Goals">
                  <Grid lg={3}>
                    {prospectData?.member_goals.map((goal, index) => {
                      return (
                        <Goal
                          onView={() => {
                            handleViewCustomerDetail({
                              title: goal.goal.name,
                              data: goal,
                            });
                          }}
                          mode="read"
                          data={goal as any}
                          key={index}
                          actions={
                            [
                              // {
                              //   label: 'Redeem',
                              //   buttonType: 'outline',
                              //   function: () => {
                              //     setRedemptionModalIsOpen(true);
                              //   },
                              // },
                            ]
                          }
                        />
                      );
                    })}
                  </Grid>
                </Accordion>

                <Accordion id="bio" title="Bio">
                  <Grid lg={3}>
                    <LabeledValue
                      label="Account Type"
                      value={formatAccountType(prospectData?.account_type?.toString())} />
                    <LabeledValue
                      label="MF Code"
                      value={prospectData?.mf_code?.toString()} />
                  </Grid>
                  <Separator gap={26} />
                  {isJointAccount && <SectionTitle title="Applicant 1 Details"></SectionTitle>}
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      individualBioInfo,
                      prospectData?.user,
                    )} ignore={['__typename']}
                    extraAttributes={[
                      { label: "Marital Status", value: prospectData?.marital_status?.name },]}
                  >
                  </InfoDisplayGrid>
                  {/* joint account bio */}
                  {isJointAccount &&
                    <React.Fragment>
                      <Separator gap={26} />
                      <SectionTitle title="Applicant 2 Details"></SectionTitle>
                      <InfoDisplayGrid
                        object={extractObjectFields(
                          jointBioInfo,
                          prospectData?.user
                        )}
                        extraAttributes={[
                          {
                            label: 'Relationship',
                            value: prospectData?.relationship_jt,
                          },
                          {
                            label: 'Marital Status',
                            value: prospectData?.marital_status_jt?.name,
                          },
                        ]}
                      />
                    </React.Fragment>
                  }
                </Accordion>

                <Accordion id="residentialinfo" title="Residential Info">
                  {isJointAccount && <SectionTitle title="Applicant 1 Residential Info"></SectionTitle>}
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      individualResidentialInfo,
                      prospectData)}
                  />

                  {/* joint account residentialinfo */}
                  {isJointAccount &&
                    <React.Fragment>
                      <Separator gap={26} />
                      <SectionTitle title="Applicant 2 Residential Info"></SectionTitle>
                      <InfoDisplayGrid object={extractObjectFields(
                        jointResidentialInfo,
                        prospectData
                      )} />
                    </React.Fragment>
                  }
                </Accordion>

                <Accordion id="occupation" title="Occupation">
                  {isJointAccount && <SectionTitle title="Applicant 1 Occupation"></SectionTitle>}
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      individualOccupationInfo,
                      prospectData
                    )}
                  />
                  {/* joint account occupation */}
                  {isJointAccount &&
                    <React.Fragment>
                      <Separator gap={26} />
                      <SectionTitle title="Applicant 2 Occupation"></SectionTitle>
                      <InfoDisplayGrid object={extractObjectFields(
                        jointOccupationInfo,
                        prospectData
                      )} />
                    </React.Fragment>
                  }
                </Accordion>

                <Accordion id="sourceoffunds" title="Source of Funds">
                  <Grid lg={2}>
                    <div>
                      <LabeledValue
                        key={1}
                        label={formatLabel('Source Of Funds')}
                        value={memberIncomeSources}></LabeledValue>
                    </div>
                    <div>
                      <LabeledValue
                        key={2}
                        label={formatLabel('Source Of Contribution')}
                        value={memberContributionSources}></LabeledValue>
                    </div>
                  </Grid>
                </Accordion>

                <Accordion
                  id="expectedaccountactivity"
                  title="Expected Account Activity">
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      [
                        'initial_investment_amount',
                        'regular_top_up_amount',
                        'regular_withdrawal_amount',
                        'anticipated_investment_activity_top_ups',
                        'anticipated_investment_activity_withdrawals',
                      ],
                      prospectData
                    )}></InfoDisplayGrid>
                </Accordion>

                <Accordion
                  id="investmentprofile"
                  title="Investment Profile">
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      [
                        'investment_objective',
                        'investment_knowledge',
                        'risk_tolerance',
                        'investment_horizon',
                        'approximate_annual_income',
                      ],
                      prospectData?.client_investment_profile
                    )}></InfoDisplayGrid>
                </Accordion>

                <Accordion
                  id="accountmandate"
                  title="Account Manadate">
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      [
                        'account_mandate'
                      ],
                      prospectData
                    )}></InfoDisplayGrid>
                </Accordion>

                <Accordion id="statementservice" title="Statement services">
                  <InfoDisplayGrid
                    object={extractObjectFields(
                      [
                        'mode_of_statement_delivery',
                        'statement_frequency',
                      ],
                      prospectData
                    )}
                  />
                </Accordion>
                {
                  isTrustAccount &&
                  <Accordion id="intrustfor" title="In Trust For">
                    <InfoDisplayGrid object={
                      extractObjectFields(
                        trustBioInfo,
                        prospectData?.user)}
                    />
                    <Separator gap={20} />
                    <InfoDisplayGrid
                      object={extractObjectFields(
                        trustInfo,
                        prospectData
                      )}>
                    </InfoDisplayGrid>
                  </Accordion>
                }
                {
                  isIndividualAndJoint &&
                  <Accordion id="nextofkin" title="Next of Kin">
                    <InfoDisplayGrid
                      object={extractObjectFields(
                        nextofkinInfo,
                        prospectData?.next_of_kin
                      )}
                      ignore={['__typename']}></InfoDisplayGrid>
                  </Accordion>
                }
                <Accordion id="beneficiaries" title="Beneficiaries">
                  <Grid lg={3}>
                    {prospectData?.beneficiaries.map((beneficiary, index) => {
                      return (
                        <InfoCard
                          initials={beneficiary.first_name}
                          title={`${beneficiary.last_name} ${beneficiary.first_name}`}
                          subText={`${beneficiary.mobile ?? ''}, ${beneficiary.mobile2 ?? ''}`}
                          key={index}
                          action={{
                            label: 'View More',
                            function: () => {
                              // filter country of origin
                              handleViewCustomerDetail({
                                title: 'Beneficiary',
                                data: extractObjectFields(beneficiaryInfo, beneficiary),
                                ignore: ['__typename', 'country_of_origin_id']
                              });
                            },
                          }}></InfoCard>
                      );
                    })}
                  </Grid>
                </Accordion>
                <Accordion id="dependents" title="Dependents">
                  <Grid lg={3}>
                    {prospectData?.member_dependents.map((dependent, index) => {
                      return (
                        <InfoCard
                          action={{
                            label: 'View More',
                            function: () => {
                              handleViewCustomerDetail({
                                title: 'Dependent',
                                data: dependent,
                                ignore: ['__typename']
                              });
                            },
                          }}
                          initials={dependent?.name ?? ''}
                          key={index}
                          title={dependent?.name ?? ''}></InfoCard>
                      );
                    })}
                  </Grid>
                </Accordion>
                <Accordion id="paymentchannels" title="Payment Channels">
                  <Grid lg={3}>
                    {prospectData?.member_payment_instructions.filter(paymentInfo => paymentInfo.payment_provider.id !== 30)?.map(
                      (instruction, index) => {
                        return (
                          <InfoCard
                            action={{
                              label: 'View More',
                              function: () => {
                                handleViewCustomerDetail({
                                  title: 'Payment Channel',
                                  data: instruction,
                                  ignore: ['__typename']
                                });
                              },
                            }}
                            key={index}
                            title={instruction.payment_provider.name ?? ''}
                            subText={instruction?.account_number?.length ? `${instruction?.account_number}` : `${instruction?.mobile_money_number}`}
                            icon="building-columns"></InfoCard>
                        );
                      }
                    )}
                  </Grid>
                </Accordion>
                <Accordion id="modeofinstruction" title="Mode of Instruction">
                  <Grid lg={2}>
                    <div>
                      <LabeledValue
                        key={1}
                        label={formatLabel('Mode of Instruction')}
                        value={memberModeOfInstructions}></LabeledValue>
                    </div>
                  </Grid>
                </Accordion>
                <Accordion id="identification" title="Identification">
              <Grid lg={3}>
                {prospectData?.member_identifications.map((id, index) => {
                  return (
                    <InfoCard
                      action={{
                        label: 'View More',
                        function: () => {
                          handleViewCustomerDetail({
                            title: 'Identification',
                            render: () => {
                              return (
                                <div>
                                  <div
                                    css={{
                                      width: '300px',
                                      height: '200px',
                                      background: theme.colors.gray[100],
                                      borderRadius: '4px',
                                      marginBottom: '36px',
                                      overflow: 'hidden',
                                    }}>
                                    <img 
                                      width="100%" 
                                      src={idImageUrls[id.id] || ""} 
                                      alt="ID Card" 
                                      onError={async (e) => {
                                        if (id.identification_card_image_url) {
                                          const url = await getSignedUrl(id.identification_card_image_url);
                                          if (url) {
                                            setIdImageUrls(prev => ({ ...prev, [id.id]: url }));
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                  <InfoDisplayGrid
                                    object={id}
                                    ignore={['__typename', "identification_card_image_url"]}
                                  />
                                </div>
                              );
                            },
                          });
                        },
                      }}
                      icon="id-card"
                      key={index}
                      title={id.identification_type.name}
                      subText={id.identification_number}
                    />
                  );
                })}

                <InfoCard
                  action={{
                    label: 'View Signature',
                    function: () => {
                      handleViewCustomerDetail({
                        title: 'Signature',
                        render: () => {
                          return (
                            <Flex jc="center">
                              <div
                                css={{
                                  width: '300px',
                                  height: '300px',
                                  background: theme.colors.gray[200],
                                  borderRadius: '4px',
                                }}>
                                {signatureUrl.includes('pdf') ? (
                                  <iframe
                                    src={signatureUrl}
                                    width="100%"
                                    height={300}
                                    title="signature"
                                  />
                                ) : (
                                  <img
                                    src={signatureUrl}
                                    width="100%"
                                    height={300}
                                    alt="signature"
                                    onError={async () => {
                                      if (prospectData?.signature) {
                                        const url = await getSignedUrl(prospectData.signature);
                                        if (url) setSignatureUrl(url);
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            </Flex>
                          );
                        },
                      });
                    },
                  }}
                  icon="signature"
                  title="Signature"
                />

                <InfoCard
                  action={{
                    label: 'View KYC Form',
                    function: () => {
                      handleViewCustomerDetail({
                        title: 'KYC Form',
                        render: () => {
                          return (
                            <Flex jc="center" styles={{ gap: "10px", flexWrap: 'wrap' }}>
                              {kycUrls.map((file, index) => {
                                if (file.type === 'application/pdf') {
                                  return (
                                    <div
                                      key={index}
                                      css={{
                                        width: '300px',
                                        height: '300px',
                                        background: theme.colors.gray[200],
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                      }}>
                                      <iframe
                                        src={file.url}
                                        width={300}
                                        height={300}
                                        title={`KYC Form PDF ${index + 1}`}
                                        style={{ border: 'none' }}
                                      />
                                    </div>
                                  );
                                } else if (['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
                                  return (
                                    <div
                                      key={index}
                                      css={{
                                        width: '300px',
                                        height: '300px',
                                        background: theme.colors.gray[200],
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                      }}>
                                      <img
                                        width="100%"
                                        height={300}
                                        src={file.url}
                                        alt={`KYC Form ${index + 1}`}
                                        style={{ objectFit: 'contain', height: '100%' }}
                                      />
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div
                                      key={index}
                                      css={{
                                        width: '300px',
                                        height: '50px',
                                        background: theme.colors.gray[200],
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                      <a
                                        href={file.url}
                                        download
                                        css={{
                                          color: theme.colors.primary,
                                          textDecoration: 'none',
                                          fontWeight: 'bold',
                                        }}>
                                        Download File {index + 1}
                                      </a>
                                    </div>
                                  );
                                }
                              })}
                            </Flex>
                          );
                        },
                      });
                    },
                  }}
                  icon="id-card"
                  title="KYC Form"
                />
              </Grid>
            </Accordion>
              </Stack>
            </div>
          </GridItem>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default ViewCustomer;
